import { useState, useEffect } from 'react';

/**
 * ADMIN AUTHENTICATION HOOK
 * 
 * Purpose: Manage admin authentication state
 * - Check for existing admin session
 * - Handle session expiration
 * - Provide admin status throughout the app
 * 
 * Features:
 * - Session persistence across browser sessions
 * - Automatic logout on session expiry
 * - Clean session management
 */

const useAdmin = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing admin session on mount
  useEffect(() => {
    checkAdminSession();
  }, []);

  const checkAdminSession = () => {
    try {
      const sessionData = localStorage.getItem('jaswanth_admin_session');
      
      if (sessionData) {
        const session = JSON.parse(sessionData);
        const now = Date.now();
        
        // Check if session is still valid (within 24 hours)
        if (session.authenticated && (now - session.timestamp) < session.expiresIn) {
          setIsAdmin(true);
        } else {
          // Session expired, clear it
          clearAdminSession();
        }
      }
    } catch (error) {
      console.error('Error checking admin session:', error);
      clearAdminSession();
    } finally {
      setIsLoading(false);
    }
  };

  const loginAdmin = (authenticated) => {
    setIsAdmin(authenticated);
  };

  const logoutAdmin = () => {
    clearAdminSession();
    setIsAdmin(false);
  };

  const clearAdminSession = () => {
    localStorage.removeItem('jaswanth_admin_session');
  };

  // Utility function to check if current user has admin privileges
  const hasAdminAccess = () => {
    return isAdmin;
  };

  // Auto-logout on session expiry (check every minute)
  useEffect(() => {
    if (isAdmin) {
      const interval = setInterval(() => {
        checkAdminSession();
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  return {
    isAdmin,
    isLoading,
    loginAdmin,
    logoutAdmin,
    hasAdminAccess,
    checkAdminSession
  };
};

export default useAdmin;